import { type ReactNode } from 'react'

import { BedBadge, type BedBadgeProps } from './BedBadge'
import { Icon } from './Icon'
import { Image } from './Image'
import { cn } from '../lib/cn'
import { hospitalPhotos } from '../lib/hospitalImages'

export type HospitalCardBed = Pick<
  BedBadgeProps,
  'tone' | 'label' | 'free' | 'total'
>

export interface HospitalCardProps {
  hospitalId: string
  /**
   * Hospital slug, used to find that hospital's own photograph (M17-T18b).
   * Omitted or unmapped keeps the neutral placeholder.
   */
  slug?: string
  name: string
  /** Area / locality line, e.g. "Tema". */
  area: string
  /** Distance label, e.g. "2.4 km" — optional (only when geolocated). */
  distance?: string
  /** Show the vetted-partner "Verified" badge by the name. */
  verified?: boolean
  beds: HospitalCardBed[]
  services: string[]
  /** Accent colour class for the initial mark (default brand blue). */
  accentClassName?: string
  onSelect?: (hospitalId: string) => void
  onSave?: (hospitalId: string) => void
  className?: string
}

export function HospitalCard({
  hospitalId,
  slug,
  name,
  area,
  distance,
  verified,
  beds,
  services,
  accentClassName = 'bg-accent',
  onSelect,
  onSave,
  className,
}: HospitalCardProps): ReactNode {
  const photos = slug ? hospitalPhotos(slug) : null

  return (
    <article
      className={cn(
        'group relative rounded-r3 border border-line-1 bg-bg-app shadow-card transition-[box-shadow,transform] hover:-translate-y-0.5 hover:border-line-2 hover:shadow-lift',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onSelect?.(hospitalId)}
        className="grid w-full grid-cols-[88px_1fr] gap-3 rounded-r3 p-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        <div className="relative">
          {/* M17-T18b — the same 88px well, now carrying the hospital's own
              photograph. Geometry is unchanged; a hospital with no mapped
              photograph keeps the neutral placeholder exactly as before. */}
          {photos ? (
            <Image
              asset={photos.main.asset}
              alt={photos.main.alt}
              className="h-[88px] w-full rounded-r3"
            />
          ) : (
            <div className="grid h-[88px] place-items-center overflow-hidden rounded-r3 bg-gradient-to-br from-bg-soft to-bg-sunken text-ink-4">
              <Icon name="bed" size={26} />
            </div>
          )}
          <span
            className={cn(
              'absolute left-1.5 top-1.5 grid h-[22px] w-[22px] place-items-center rounded-md text-[11px] font-bold text-white shadow-xs ring-1 ring-black/5',
              accentClassName,
            )}
            aria-hidden="true"
          >
            {name.charAt(0)}
          </span>
        </div>

        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              {/* PORTED — fgType.lg at weight 500 (65:4569). The incumbent was
                  text-sm/semibold; upstream names are 18/28 Medium. */}
              <span className="truncate text-[18px] font-medium leading-7 text-ink-1">
                {name}
              </span>
              {verified ? (
                <span
                  className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-accent-bg px-1.5 py-0.5 text-[10px] font-semibold text-accent-ink ring-1 ring-inset ring-accent/15"
                  title="Verified AMTC partner"
                >
                  <Icon name="badge" size={10} /> Verified
                </span>
              ) : null}
            </div>
            {/* PORTED — the area line is fgType.md on --ink-2, with a pin for
                the locality and a nav glyph for the distance, separated by a
                4px dot (65:4569). The incumbent ran both at 11px on ink-3. */}
            <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[14px] leading-5 text-ink-2">
              <Icon name="pin" size={14} />
              <span>{area}</span>
              {distance ? (
                <>
                  <span
                    aria-hidden="true"
                    className="h-1 w-1 rounded-full bg-ink-4"
                  />
                  <Icon name="nav" size={14} />
                  <span className="tabular-nums">{distance}</span>
                </>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap gap-1">
            {beds.map((b) => (
              <BedBadge key={b.label} {...b} />
            ))}
          </div>

          <div className="flex flex-wrap gap-1 text-[11px] text-ink-2">
            {services.map((s) => (
              <span
                key={s}
                className="rounded-full border border-line-1 bg-bg-soft px-1.5 py-0.5"
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      </button>

      <button
        type="button"
        aria-label={`Save ${name}`}
        onClick={() => onSave?.(hospitalId)}
        className="absolute right-2.5 top-2.5 rounded-full bg-bg-app/70 p-1.5 text-ink-3 backdrop-blur-sm hover:scale-110 hover:bg-bg-soft hover:text-bed-bad focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        <Icon name="heart" size={16} />
      </button>
    </article>
  )
}
